import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../utils/api';
import {
  ReviewCycle,
  User,
  QuestionTemplate,
  Nomination,
  Review360Response,
  Review360Report,
  CycleProgress,
  TrendPoint,
  Review360Answer,
  Review360DashboardData,
} from '../types';

export const useReview360Dashboard = () =>
  useQuery({
    queryKey: ['review-360-dashboard'],
    queryFn: async (): Promise<Review360DashboardData> => api('/review-360/me/dashboard'),
    staleTime: 30_000,
  });

// === Cycles ===

export const useReview360Cycles = (filters?: { status?: string }) => {
  return useQuery({
    queryKey: ['review-360-cycles', filters],
    queryFn: async (): Promise<ReviewCycle[]> => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      const query = params.toString();
      return api(`/review-360/cycles${query ? `?${query}` : ''}`);
    },
  });
};

export const useReview360Cycle = (id: string) => {
  return useQuery({
    queryKey: ['review-360-cycles', id],
    queryFn: async (): Promise<ReviewCycle> => api(`/review-360/cycles/${id}`),
    enabled: !!id,
  });
};

export const useCreateReview360Cycle = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<ReviewCycle>) =>
      api('/review-360/cycles', { method: 'POST', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-360-cycles'] }),
  });
};

export const useUpdateReview360Cycle = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ReviewCycle> }) =>
      api(`/review-360/cycles/${id}`, { method: 'PATCH', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-360-cycles'] }),
  });
};

export const useTransitionReview360Status = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) =>
      api(`/review-360/cycles/${id}/status`, { method: 'PATCH', body: { status } }),
    // Переход сам создаёт номинации и ответы. Страница цикла держит их список с
    // черновика, поэтому без сброса новый статус показывался с пустыми номинациями.
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['review-360-cycles'] });
      qc.invalidateQueries({ queryKey: ['review-360-nominations', id] });
      qc.invalidateQueries({ queryKey: ['review-360-progress', id] });
    },
  });
};

export const useDeleteReview360Cycle = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api(`/review-360/cycles/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-360-cycles'] }),
  });
};

/** Кого текущий человек может взять в цикл — охват review360.run, считает сервер. */
export const useReview360People = () =>
  useQuery({
    queryKey: ['review-360-people'],
    queryFn: async (): Promise<User[]> => api('/review-360/people'),
  });

// === Templates ===

export const useQuestionTemplates = () => {
  return useQuery({
    queryKey: ['review-360-templates'],
    queryFn: async (): Promise<QuestionTemplate[]> => api('/review-360/templates'),
  });
};

export const useQuestionTemplate = (id: string) => {
  return useQuery({
    queryKey: ['review-360-templates', id],
    queryFn: async (): Promise<QuestionTemplate> => api(`/review-360/templates/${id}`),
    enabled: !!id,
  });
};

export const useCreateQuestionTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<QuestionTemplate>) =>
      api('/review-360/templates', { method: 'POST', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-360-templates'] }),
  });
};

export const useUpdateQuestionTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<QuestionTemplate> }) =>
      api(`/review-360/templates/${id}`, { method: 'PATCH', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-360-templates'] }),
  });
};

export const useDeleteQuestionTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api(`/review-360/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-360-templates'] }),
  });
};

export const useDuplicateQuestionTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<QuestionTemplate> =>
      api(`/review-360/templates/${id}/duplicate`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-360-templates'] }),
  });
};

export const useGenerateTemplateFromMap = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mapId: string) =>
      api(`/review-360/templates/from-map/${mapId}`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-360-templates'] }),
  });
};

// === Nominations ===

export const useNominations = (cycleId: string) => {
  return useQuery({
    queryKey: ['review-360-nominations', cycleId],
    queryFn: async (): Promise<Nomination[]> => api(`/review-360/cycles/${cycleId}/nominations`),
    enabled: !!cycleId,
  });
};

export const useAddNomination = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cycleId,
      ...data
    }: {
      cycleId: string;
      subjectId: string;
      raterId: string;
      raterRole: string;
    }) => api(`/review-360/cycles/${cycleId}/nominations`, { method: 'POST', body: data }),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['review-360-nominations', vars.cycleId] }),
  });
};

export const useUpdateNominationStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cycleId,
      nomId,
      status,
    }: {
      cycleId: string;
      nomId: string;
      status: string;
    }) =>
      api(`/review-360/cycles/${cycleId}/nominations/${nomId}`, {
        method: 'PATCH',
        body: { status },
      }),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['review-360-nominations', vars.cycleId] }),
  });
};

// === Responses ===

export const useMyResponses = (cycleId: string) => {
  return useQuery({
    queryKey: ['review-360-my-responses', cycleId],
    queryFn: async (): Promise<Review360Response[]> =>
      api(`/review-360/cycles/${cycleId}/responses/my`),
    enabled: !!cycleId,
  });
};

export const useReview360ResponseDetail = (cycleId: string, responseId: string) => {
  return useQuery({
    queryKey: ['review-360-response', responseId],
    queryFn: async (): Promise<Review360Response> =>
      api(`/review-360/cycles/${cycleId}/responses/${responseId}`),
    enabled: !!cycleId && !!responseId,
  });
};

export const useSaveResponseDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cycleId,
      responseId,
      answers,
    }: {
      cycleId: string;
      responseId: string;
      answers: Review360Answer[];
    }) =>
      api(`/review-360/cycles/${cycleId}/responses/${responseId}`, {
        method: 'PUT',
        body: { answers },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['review-360-response', vars.responseId] });
      qc.invalidateQueries({ queryKey: ['review-360-my-responses', vars.cycleId] });
      qc.invalidateQueries({ queryKey: ['review-360-dashboard'] });
    },
  });
};

export const useSubmitResponse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ cycleId, responseId }: { cycleId: string; responseId: string }) =>
      api(`/review-360/cycles/${cycleId}/responses/${responseId}/submit`, { method: 'POST' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['review-360-response', vars.responseId] });
      qc.invalidateQueries({ queryKey: ['review-360-my-responses', vars.cycleId] });
      qc.invalidateQueries({ queryKey: ['review-360-dashboard'] });
    },
  });
};

// === Progress ===

export const useCycleProgress = (cycleId: string) => {
  return useQuery({
    queryKey: ['review-360-progress', cycleId],
    queryFn: async (): Promise<CycleProgress> => api(`/review-360/cycles/${cycleId}/progress`),
    enabled: !!cycleId,
  });
};

// === Reports ===

export const useReview360Report = (cycleId: string, subjectId: string) => {
  return useQuery({
    queryKey: ['review-360-report', cycleId, subjectId],
    queryFn: async (): Promise<Review360Report> =>
      api(`/review-360/cycles/${cycleId}/report/${subjectId}`),
    enabled: !!cycleId && !!subjectId,
    // 403/404/409 — ответ окончательный (например, цикл ещё не завершён), повтор ничего не даст.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failureCount < 1,
  });
};

export const useReview360Trend = (subjectId: string) => {
  return useQuery({
    queryKey: ['review-360-trend', subjectId],
    queryFn: async (): Promise<TrendPoint[]> => api(`/review-360/subjects/${subjectId}/trend`),
    enabled: !!subjectId,
  });
};
